import {database, getPushStatusAsync} from "../firebase";

/*
 * Realtime Database connection state, for the chat list.
 *
 * Why this exists: a PCA's chat list is fed by a live connection to
 * /openconversations, but the bell that rings for a new message is an FCM push
 * delivered through the service worker — a different channel. On a weak or
 * dropping connection the bell keeps ringing while the list silently stops
 * updating, and nothing on screen says so (prod, 2026-10-06, chat
 * -P3H2RJYEyb5v7Hn74O6: assigned 16:30:54, not shown until a reload at 16:38
 * on a 3G connection).
 *
 * This records every disconnect and reconnect in networkLogs/{doctorID} — the
 * node ChatBuilder already writes on each load — so the next report shows
 * whether the portal was connected, and calls back on reconnect so the list
 * can be re-read instead of waiting for someone to reload.
 *
 * It also checks the list is current when the connection only *says* it is up
 * (see checkChatListFreshness): prod, 2026-10-11 and 2026-10-12, chats
 * -P3h2LAlJDEEOk8gd3pj and -P3mWH7LsMqbM-Z2DCWo each sat unseen for 27 minutes
 * in a portal that logged no disconnect and no reload, while every bell push
 * for them was accepted by FCM.
 *
 * And it checks the list's contents (onVerify, ConversationAction.verifyChatList):
 * prod, 2026-11-04, -P5Z3u5xs0nA0FM2CMKV was missing from a list whose
 * connection answered the freshness check at once, because the list's own
 * listeners had stopped. A healthy connection now leads to that check too, a
 * push about a chat the list does not show is checked at once, and the list is
 * checked every few minutes while the page is shown.
 */

/* Shorter blips are ordinary and the SDK replays what it missed; past this the
   list is re-read in full on reconnect. */
const RESYNC_AFTER_MS = 10000;

/* `.info/connected` starts false while the first connection is made. Grace
   before that initial wait counts as an outage worth showing. */
const INITIAL_GRACE_MS = 10000;

/* A freshness check runs at most this often: a busy PCA gets a push for every
   patient message. */
const CHECK_MIN_INTERVAL_MS = 30000;

/* How long a check waits for the server to acknowledge its write before
   treating the connection as stale. Generous, because a false alarm costs a
   reconnect on what may already be a slow line. */
const ACK_TIMEOUT_MS = 8000;

/* After a forced reconnect, how long to wait for the server before giving up
   and leaving recovery to the ordinary reconnect path above. */
const REOPEN_TIMEOUT_MS = 60000;

/* A tab hidden at least this long is checked when it is shown again. */
const HIDDEN_CHECK_AFTER_MS = 60000;

/* The heartbeat ticks this often. Browsers run background timers as rarely as
   once a minute, so only a gap well past that means the page was paused (tab
   put to sleep, frozen, or the computer asleep). */
const HEARTBEAT_MS = 15000;
const HEARTBEAT_GAP_MS = 120000;

/* While the page is shown, its list is compared with the server this often,
   even without a push (a chat assigned straight to a PCA used to send none).
   Not written to networkLogs unless something is missing. */
const PERIODIC_VERIFY_MS = 180000;

/* Pushes that mean "a chat was just given to you" (cloudfunctions
   functionsV2/assignmentPush.js). */
const ASSIGNMENT_PUSH_TYPES = new Set(['joined_chat', 'transfer_chat']);

export const logConnectionEvent = (doctorID, event) => {
    if (!doctorID) return;
    const at = Date.now();
    /* Written while disconnected, the SDK queues this and sends it on
       reconnect, keyed by the time it actually happened. */
    database
        .ref(`networkLogs/${doctorID}/${at}`)
        .set({...event, timestamp: at})
        .catch(() => {});
};

/**
 * Logs an event and resolves with how long the server took to acknowledge it,
 * or null if it did not within `timeoutMs`.
 *
 * The acknowledgement is the point: the server answers on the same connection,
 * in order, after everything it sent before. An answer means every change made
 * before this write — a chat assigned, then a push sent about it — has already
 * reached this page.
 */
const logAcknowledged = (doctorID, event, timeoutMs) => {
    const at = Date.now();
    const write = database
        .ref(`networkLogs/${doctorID}/${at}`)
        .set({...event, timestamp: at})
        .then(() => Date.now() - at, () => null);
    const timeout = new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs));
    return Promise.race([write, timeout]);
};

/* The monitor that is running, so a push handled elsewhere (App.js) can ask
   it for a check. One chat list is shown at a time. */
let activeMonitor = null;

/**
 * Makes sure the chat list is as current as the server, when there is reason
 * to doubt it: a push arrived, or the page was paused and is running again.
 *
 * `.info/connected` can stay true over a connection that delivers nothing, and
 * a re-read then comes from the local cache, so neither notices a stale list.
 * Instead this writes `reason` to networkLogs and waits for the server to
 * acknowledge it. No answer: the connection is dropped and reopened, which
 * makes the SDK fetch every listened path again, and once the server answers
 * on the new connection the list is re-read.
 */
export const checkChatListFreshness = (reason, details = {}) => {
    const monitor = activeMonitor;
    /* Known to be offline: the reconnect path above already shows the banner
       and re-reads the list when the connection comes back. */
    if (!monitor || !monitor.connected) return;
    const now = Date.now();
    /* A push saying a chat was just given to this PCA, about a chat the list
       does not show, is checked whatever the interval: that is exactly the chat
       they are about to look for. Only those kinds: other pushes can be about
       chats the list leaves out on purpose (clinical, SP reviews, closed). */
    const unlisted = Boolean(
        ASSIGNMENT_PUSH_TYPES.has(details.type) &&
        details.conversationID && monitor.isListed && !monitor.isListed(details.conversationID)
    );
    if (monitor.checking || (!unlisted && now - monitor.lastCheckAt < CHECK_MIN_INTERVAL_MS)) return;
    monitor.checking = true;
    monitor.lastCheckAt = now;

    const {doctorID} = monitor;
    logAcknowledged(doctorID, {event: reason, ...details, ...(unlisted ? {unlisted: true} : {})}, ACK_TIMEOUT_MS)
        .then(async (ackMs) => {
            if (ackMs !== null) {
                /* The connection delivers; make sure the list's listeners still
                   do (they can be cancelled while the connection stays up). */
                if (monitor === activeMonitor && monitor.onVerify) {
                    monitor.lastVerifyAt = Date.now();
                    monitor.onVerify(reason, details);
                }
                return;
            }
            logConnectionEvent(doctorID, {event: 'staleConnection', reason, waitedMs: ACK_TIMEOUT_MS});
            database.goOffline();
            database.goOnline();
            const reopenMs = await logAcknowledged(doctorID, {event: 'staleConnectionReopened', reason}, REOPEN_TIMEOUT_MS);
            if (reopenMs !== null && monitor === activeMonitor && monitor.onStale) monitor.onStale(reason);
        })
        .finally(() => {
            monitor.checking = false;
        });
};

/**
 * Watches the connection. `onState(connected)` drives the banner;
 * `onReconnect(outageMs)` fires after an outage long enough to re-read;
 * `onStale(reason)` fires after a freshness check had to reopen the connection;
 * `onPushStatus(status)` reports whether this browser can get pushes at all;
 * `onVerify(reason, details)` compares the list with the server (see above);
 * `isListed(conversationID)` says whether the list shows a chat.
 * Returns a function that stops watching.
 */
export const startConnectionMonitor = (doctorID, {onState, onReconnect, onStale, onPushStatus, onVerify, isListed} = {}) => {
    const ref = database.ref('.info/connected');
    const startedAt = Date.now();
    let everConnected = false;
    let disconnectedAt = null;
    const monitor = {doctorID, onStale, onVerify, isListed, connected: false, checking: false, lastCheckAt: 0, lastVerifyAt: Date.now()};

    /* Not connected within the grace period of opening: say so, rather than
       waiting for a first connection that may never come. */
    const graceTimer = setTimeout(() => {
        if (!everConnected) {
            disconnectedAt = startedAt;
            onState && onState(false);
            logConnectionEvent(doctorID, {event: 'notConnectedAfterOpen', waitedMs: Date.now() - startedAt});
        }
    }, INITIAL_GRACE_MS);

    const listener = ref.on('value', (snap) => {
        const connected = snap.val() === true;
        if (connected) {
            const outageMs = disconnectedAt ? Date.now() - disconnectedAt : 0;
            const firstConnect = !everConnected;
            everConnected = true;
            disconnectedAt = null;
            monitor.connected = true;
            onState && onState(true);
            logConnectionEvent(doctorID, {
                event: firstConnect ? 'connected' : 'reconnected',
                outageMs,
                sinceOpenMs: Date.now() - startedAt,
            });
            if (!firstConnect && outageMs >= RESYNC_AFTER_MS && onReconnect) onReconnect(outageMs);
        } else if (everConnected) {
            disconnectedAt = Date.now();
            monitor.connected = false;
            onState && onState(false);
            logConnectionEvent(doctorID, {event: 'disconnected'});
        }
    });

    activeMonitor = monitor;

    /* Whether pushes can reach this browser: with notifications blocked there
       is no token and so no bell, which looks exactly like a missed chat. */
    let stopped = false;
    getPushStatusAsync().then((status) => {
        if (stopped) return;
        logConnectionEvent(doctorID, {event: 'pushStatus', ...status});
        onPushStatus && onPushStatus(status);
    });

    /* A paused page runs nothing — no list updates, no in-page bell — and
       catches up only when it runs again, so each way of noticing that it ran
       again asks for a check. */
    let lastBeatAt = Date.now();
    const heartbeat = setInterval(() => {
        const gapMs = Date.now() - lastBeatAt;
        lastBeatAt = Date.now();
        if (gapMs >= HEARTBEAT_GAP_MS) checkChatListFreshness('pageResumed', {gapMs, via: 'heartbeat'});
        if (
            onVerify && monitor.connected && !monitor.checking && !document.hidden &&
            Date.now() - monitor.lastVerifyAt >= PERIODIC_VERIFY_MS
        ) {
            monitor.lastVerifyAt = Date.now();
            onVerify('periodic', {});
        }
    }, HEARTBEAT_MS);

    let frozenAt = null;
    const onFreeze = () => { frozenAt = Date.now(); };
    const onResume = () => {
        checkChatListFreshness('pageResumed', {gapMs: frozenAt ? Date.now() - frozenAt : null, via: 'resume'});
        frozenAt = null;
    };

    let hiddenAt = document.hidden ? Date.now() : null;
    const onVisibility = () => {
        if (document.hidden) {
            hiddenAt = Date.now();
            return;
        }
        const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
        hiddenAt = null;
        if (hiddenMs >= HIDDEN_CHECK_AFTER_MS) checkChatListFreshness('pageShown', {hiddenMs});
    };

    /* While the tab is in the background, pushes go to the service worker,
       which shows the notification and tells open tabs (it used to send a
       bare string; both forms count). */
    const onServiceWorkerMessage = (e) => {
        const data = e && e.data;
        const fromPush = (data && data.source === 'fcm') || data === 'Responding to ';
        if (fromPush) {
            checkChatListFreshness('pushReceived', {
                type: (data && data.type) || null,
                inBackground: true,
                conversationID: (data && data.conversationID) || null,
            });
        }
    };
    const serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;

    document.addEventListener('freeze', onFreeze);
    document.addEventListener('resume', onResume);
    document.addEventListener('visibilitychange', onVisibility);
    serviceWorker && serviceWorker.addEventListener('message', onServiceWorkerMessage);

    return () => {
        stopped = true;
        clearTimeout(graceTimer);
        clearInterval(heartbeat);
        ref.off('value', listener);
        document.removeEventListener('freeze', onFreeze);
        document.removeEventListener('resume', onResume);
        document.removeEventListener('visibilitychange', onVisibility);
        serviceWorker && serviceWorker.removeEventListener('message', onServiceWorkerMessage);
        if (activeMonitor === monitor) activeMonitor = null;
    };
};
